import { useState } from "react";
import { AlertTriangle, Pencil } from "lucide-react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Disclaimer, Kpi, PageHeader, Section, Spinner } from "@/components/kit";
import { FormDialog } from "@/components/ResourceTable";
import { useWsData } from "@/hooks/useWsData";
import { api, brl, brlShort, pct } from "@/lib/api";
import { useWs } from "@/context/WorkspaceContext";

const FIELDS = [
  ["revenue", "Faturamento mensal (R$)"], ["variable_costs", "Custos variáveis/mês (R$)"], ["fixed_costs", "Despesas fixas/mês (R$)"], ["tax_rate_pct", "Alíquota de impostos (%)"],
  ["pro_labore", "Pró-labore (R$/mês)"], ["current_assets", "Ativo circulante (R$)"], ["current_liabilities", "Passivo circulante (R$)"], ["receivables", "Contas a receber (R$)"],
  ["overdue_receivables", "Recebíveis vencidos (R$)"], ["inventory", "Estoque (R$)"], ["payables", "Contas a pagar a fornecedores (R$)"],
  ["financial_costs", "Despesas financeiras/mês (juros, tarifas) (R$)"], ["income_tax", "IR/CSLL e impostos sobre o lucro/mês (R$)"],
].map(([name, label]) => ({ name, label, type: "number" }));

export default function Empresa() {
  const { ws } = useWs();
  const { data, reload, wid } = useWsData("/business");
  const [open, setOpen] = useState(false);
  if (!data) return <Spinner />;
  const m = data.metrics;
  const save = async (p) => { await api.put(`/workspaces/${wid}/business`, p); toast.success("Dados empresariais salvos"); reload(); };
  const isBiz = ["empresa", "autonomo"].includes(ws?.kind);
  const auto = data.auto || [];
  const manualFields = FIELDS.filter((f) => !auto.includes(f.name));
  const autoLabels = FIELDS.filter((f) => auto.includes(f.name)).map((f) => f.label.replace(/ \(.*\)/, "").replace("/mês", ""));
  return (
    <div data-testid="empresa-page">
      <PageHeader eyebrow="PJ · Autônomo" title="Gestão empresarial" subtitle="Lucro, margem, ponto de equilíbrio, capital de giro e inadimplência do seu negócio.">
        <Button onClick={() => setOpen(true)} data-testid="edit-business-btn"><Pencil className="mr-1 h-4 w-4" /> Atualizar dados</Button>
      </PageHeader>
      {!isBiz && <div className="mb-6 rounded-xl border border-sky-500/30 bg-sky-500/10 p-4 text-sm text-sky-200" data-testid="business-kind-hint">Dica: este módulo é ideal para espaços do tipo Empresa ou Autônomo. Selecione um no topo ou crie um novo.</div>}
      {data.auto_month && (
        <div className="mb-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200" data-testid="business-auto-hint">
          Preenchido automaticamente com os lançamentos de {data.auto_month.slice(5)}/{data.auto_month.slice(0, 4)}: <b>{autoLabels.join(", ")}</b>.
          Em “Atualizar dados” você informa só o que não aparece nos lançamentos (pró-labore, contas a receber, estoque, fornecedores).
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Faturamento" value={brl(m.revenue)} tone="green" testid="biz-revenue" />
        <Kpi label="Lucro líquido" value={brl(m.net_profit)} tone={m.net_profit >= 0 ? "green" : "red"} hint={`Margem líquida ${pct(m.net_margin_pct)}`} testid="biz-profit" />
        <Kpi label="Margem de contribuição" value={brl(m.contribution_margin)} tone="gold" hint={pct(m.contribution_margin_pct)} testid="biz-cm" />
        <Kpi label="Ponto de equilíbrio" value={m.breakeven_revenue ? brl(m.breakeven_revenue) : "—"} hint="Faturamento mínimo/mês" testid="biz-breakeven" />
        <Kpi label="Capital de giro" value={brl(m.working_capital)} tone={m.working_capital >= m.ncg ? "green" : "red"} testid="biz-wc" />
        <Kpi label="Necessidade de capital de giro" value={brl(m.ncg)} testid="biz-ncg" />
        <Kpi label="Impostos estimados" value={brl(m.taxes)} tone="red" testid="biz-taxes" />
        <Kpi label="Inadimplência" value={pct(m.default_rate_pct)} tone={m.default_rate_pct > 5 ? "red" : "green"} testid="biz-default" />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Section title="Fluxo de caixa (lançamentos)" className="lg:col-span-2" testid="biz-cashflow">
          <div className="h-72">
            <ResponsiveContainer>
              <ComposedChart data={data.cash_flow}>
                <CartesianGrid stroke="#1E3A6E" strokeDasharray="3 3" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} tickFormatter={brlShort} width={70} />
                <Tooltip contentStyle={{ background: "#0B1A33", border: "1px solid #1E3A6E" }} formatter={(v) => brl(v)} /><Legend />
                <Bar dataKey="receitas" name="Entradas" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="despesas" name="Saídas" fill="#EF4444" radius={[4, 4, 0, 0]} />
                <Line dataKey="acumulado" name="Saldo acumulado" stroke="#D4AF37" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Alertas gerenciais" testid="biz-alerts">
          {m.alerts.length ? m.alerts.map((a) => <div key={a} className="mb-3 flex gap-2 text-sm text-amber-200"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />{a}</div>)
            : <p className="text-sm text-emerald-300">Indicadores dentro dos parâmetros de referência.</p>}
        </Section>
      </div>
      <Disclaimer text={m.disclaimer} />
      <FormDialog open={open} onOpenChange={setOpen} title="Dados que não vêm dos lançamentos" fields={manualFields} testid="business-dialog"
        initial={Object.fromEntries(manualFields.map((f) => [f.name, data.inputs[f.name] ?? 0]))} onSubmit={save} />
    </div>
  );
}
